// src/pages/vendor/Settings/PickupAddressSettings.tsx

import React, { useState, useEffect } from 'react';
import { MapPin, Plus } from 'lucide-react';
import { vendorAPI } from '../../../api/vendor';
import { addressAPI } from '../../../api/addresses';
import AddressCard from '../../../components/address/AddressCard';
import AddressForm from '../../../components/address/AddressForm';
import type { Address, City, Vendor } from '../../../types';
import pickupsAPI from '../../../api/pickup';

interface PickupAddressSettingsProps {
    vendor: Vendor | null;
}

const PickupAddressSettings: React.FC<PickupAddressSettingsProps> = ({ vendor }) => {
    const [addresses, setAddresses] = useState<Address[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingAddress, setEditingAddress] = useState<Address | null>(null);
    const [cities, setCities] = useState<City[]>([]);

    useEffect(() => {
        fetchCities();
        fetchAddresses();
    }, []);

    const fetchCities = async () => {
        try {
            const response = await addressAPI.getCities();
            if (response.data) {
                setCities(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch cities:', error);
        }
    };

    const fetchAddresses = async () => {
        try {
            const data = await vendorAPI.getVendorAddresses();
            setAddresses(data);
        } catch (error) {
            console.error('Failed to fetch addresses:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddAddress = () => {
        setEditingAddress(null);
        setShowForm(true);
    };

    const handleEditAddress = (address: Address) => {
        setEditingAddress(address);
        // console.log(address)
        setShowForm(true);
    };

    const handleSaveAddress = async (data: any) => {
        try {
            if (editingAddress) {
                const response = await vendorAPI.updateVendorAddress(editingAddress.id, data);
                setAddresses(prev => {
                    if (response.is_default && response.store) {
                        return prev.map(a =>
                            a.id === response.id
                                ? response
                                : a.store === response.store
                                    ? { ...a, is_default: false }
                                    : a
                        );
                    }
                    return prev.map(a => a.id === response.id ? response : a);
                });
            } else {
                const response = await vendorAPI.addVendorAddress(data);
                setAddresses(prev => {
                    if (response.is_default && response.store) {
                        // clear defaults for the same store, then add
                        const cleared = prev.map(a =>
                            a.store === response.store ? { ...a, is_default: false } : a
                        );
                        return [...cleared, response];
                    }
                    return [...prev, response];
                });
            }
            setShowForm(false);
            setEditingAddress(null);
        } catch (error) {
            console.error('Failed to save address:', error);
            throw error;
        }
    };

    const handleDeleteAddress = async (id: number) => {
        // if (!confirm('Are you sure you want to delete this address?')) return;
        try {
            await vendorAPI.deleteVendorAddress(id);
            setAddresses(addresses.filter(a => a.id !== id));
        } catch (error) {
            console.error('Failed to delete address:', error);
        }
    };

    const handleSetDefault = async (storeAddressId: number, storeId: number) => {
        // console.log(addresses[0], storeAddressId, storeId)
        // console.log(addresses[0].address, storeAddressId)
        try {
            await pickupsAPI.setDefaultStoreAddress(storeAddressId, storeId);
            setAddresses(prev => prev.map((a, i) => {
                return a.store === storeId
                    ? { ...a, is_default: a.address === storeAddressId }
                    : a
            }));
        } catch (error) {
            console.error('Failed to set default address:', error);
        }
    };

    const handleUpdateCoordinates = async (id: number, lat?: number, lng?: number) => {
        // Optimistic update - update the address in state immediately
        setAddresses(prev => prev.map(a =>
            a.id === id
                ? {
                    ...a,
                    address_detail: {
                        ...a.address_detail,
                        latitude: lat,
                        longitude: lng,
                        coordinates_saved_at: new Date().toISOString(),
                        address_verification_status: 'pending',
                    },
                }
                : a
        ));

        try {
            await vendorAPI.updateStoreAddressCoordinates(id, { latitude: lat, longitude: lng });
            // Success - state is already updated
        } catch (error) {
            console.error('Failed to update coordinates:', error);
            // Revert on error
            setAddresses(addresses.map(a =>
                a.id === id
                    ? {
                        ...a,
                        latitude: undefined,
                        longitude: undefined,
                        coordinates_saved_at: undefined,
                        address_verification_status: 'needs_location'
                    }
                    : a
            ));
        }
    };

    return (
        <div>
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-medium text-gray-900">Business Addresses</h3>
                        <p className="text-sm text-gray-500">
                            Manage your business locations for pickup requests
                        </p>
                    </div>
                    <button
                        onClick={handleAddAddress}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                        <Plus size={16} />
                        Add Pickup Address
                    </button>
                </div>

                {loading ? (
                    <div className="text-center py-8">Loading...</div>
                ) : addresses.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                        <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-500">No business addresses added yet</p>
                        <p className="text-sm text-gray-400 mt-1">Add your first address for pickup requests</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {addresses.map((address) => (
                            <AddressCard
                                key={address.id}
                                address={address.address_detail}
                                isDefault={address.is_default}
                                storeDetail={address.store_detail}
                                mode="vendor"
                                onEdit={() => handleEditAddress(address)}
                                onDelete={() => handleDeleteAddress(address.id)}
                                onSetDefault={() => handleSetDefault(address.address, address.store)}
                                storeAddressId={address.id}
                                onUpdateCoordinates={handleUpdateCoordinates}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Address Form Modal */}
            {showForm && (
                <AddressForm
                    initialData={editingAddress?.address_detail || undefined}
                    cities={cities}
                    mode="vendor"
                    onSubmit={handleSaveAddress}
                    onCancel={() => {
                        setShowForm(false);
                        setEditingAddress(null);
                    }}
                    onSkipHouseNumber={() => { }}
                />
            )}
        </div>
    );
};

export default PickupAddressSettings;